/**
 * Chat store — speaks to the new FastAPI/Anthropic backend via `innoq.ts`.
 *
 * `innochat.ts` is intentionally left untouched in /src/lib so this module
 * can be reverted with a single git revert if the new path needs to be
 * disabled in production.
 *
 * Session persistence: the current session id is stored in localStorage
 * (scoped to the signed-in user) so a page refresh RESTORES the conversation —
 * hydrateSession() reloads its messages from /db/chat/sessions/{id}/messages.
 * A logout, a user switch, an expired/not-owned session, or "New session"
 * always drops the stored id and starts fresh.
 */
import { create } from 'zustand';
import { streamChat, submitFeedback, type Citation, type DashboardState, type DebugInfo, type FeedbackCategory, type FilterSuggestion, type LinkablePerson } from '../lib/innoq';
import { getChatSession, type ChatHistoryMessage } from '../lib/chatHistoryApi';
import { useAnchorStore } from './anchorStore';

// ── Session persistence (localStorage) ──────────────────────────────────────
// One key, scoped to the user so we never restore another account's session
// (and so a logged-out browser restores nothing). The backend still validates
// ownership, so a stale/forged id just fails the fetch and we fall back to a
// fresh session.
const SESSION_KEY = 'chat_session';

interface PersistedSession { userId: string; sessionUuid: string; }

function readPersistedSession(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as PersistedSession;
    return (p && typeof p.userId === 'string' && typeof p.sessionUuid === 'string') ? p : null;
  } catch {
    return null;
  }
}

function writePersistedSession(userId: string, sessionUuid: string) {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify({ userId, sessionUuid })); } catch { /* storage full/unavailable */ }
}

function clearPersistedSession() {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

// Map a persisted history message (/db/chat/sessions/{id}/messages) into the
// live widget's ChatMessage shape. Mirrors the wiring the /chat-history page
// does, but into the store so the widget rehydrates on refresh.
function historyToChatMessages(msgs: ChatHistoryMessage[]): ChatMessage[] {
  return msgs.map(m => ({
    role:              m.role,
    text:              m.text,
    citations:         m.citations,
    linkablePeople:    m.linkable_people,
    followUps:         m.follow_ups,
    filterSuggestions: m.filter_suggestions,
    debug:             m.debug,
    messageId:         m.id,
    feedback: m.feedback
      ? {
          rating:   m.feedback.rating,
          category: (m.feedback.category as FeedbackCategory | null) ?? null,
          comment:  m.feedback.comment ?? null,
        }
      : undefined,
  }));
}

export interface ChatMessageFeedback {
  rating:   'up' | 'down';
  category?: FeedbackCategory | null;
  comment?:  string | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
  /** User-clickable navigation pills shown beneath the bubble. */
  filterSuggestions?: FilterSuggestion[];
  followUps?: string[];
  /** Inline citations referenced by [N] markers in the answer text. */
  citations?: Citation[];
  /** People-by-name safety net — councillors gathered this turn, name-matched
   *  in the answer text to attach a profile link the model didn't cite. */
  linkablePeople?: LinkablePerson[];
  /** Dev-only: per-iteration tool-call traces. Empty for non-developer users. */
  debug?: DebugInfo[];
  /** Server-assigned message id — present once the live stream completes and
   *  on any message loaded from /db/chat/sessions/{id}/messages. Used as the
   *  target for the feedback endpoint. */
  messageId?: string;
  /** User's feedback on this assistant turn (👍/👎 + optional categorised
   *  reason). Set when the user submits via the feedback UI; persisted on
   *  the server so it survives session reload. */
  feedback?: ChatMessageFeedback;
  /** True when the user hit Stop before any answer text arrived. The widget
   *  renders a localised “Stopped.” placeholder instead of a blank bubble. */
  stopped?: boolean;
}

type GetTokenFn = () => Promise<string | null | undefined>;

/** In-flight stream abort — kept outside Zustand state (not reactive). */
let activeAbort: AbortController | null = null;

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

interface ChatState {
  // ── UI state ─────────────────────────────────────────────────────────────
  isOpen: boolean;
  isWidgetEnabled: boolean;

  // ── Session state ────────────────────────────────────────────────────────
  userId: string | null;
  // Audit C1 — backend now reads the user from the verified Clerk JWT, not
  // from a body field. The store keeps `userId` for UI purposes (e.g. "is
  // the user signed in?" gating) and a `getToken` callback the chat fetches
  // call to attach `Authorization: Bearer <jwt>`.
  getToken: GetTokenFn | null;
  sessionUuid: string | null;
  messages: ChatMessage[];
  isStreaming: boolean;

  // ── Dev inspector toggle (only meaningful for users with the dev role) ──
  devInspectorEnabled: boolean;

  // ── Speed/Quality model selector (dev-role only; backend re-verifies) ───
  chatMode: 'speed' | 'quality';

  // ── Misc ─────────────────────────────────────────────────────────────────
  globalFilter: string;

  // ── Actions ──────────────────────────────────────────────────────────────
  toggleWidget: () => void;
  setWidgetEnabled: (enabled: boolean) => void;
  setUserId: (userId: string | null) => void;
  setGetToken: (getToken: GetTokenFn | null) => void;
  setDevInspectorEnabled: (enabled: boolean) => void;
  setChatMode: (mode: 'speed' | 'quality') => void;
  newSession: () => void;
  /** Restore the persisted session (if any) for the signed-in user by
   *  reloading its messages from the backend. No-op while streaming, when a
   *  conversation is already loaded, or when there's nothing valid to restore
   *  (logged out, different user, expired/not-owned session). Safe to call on
   *  every widget mount. */
  hydrateSession: () => Promise<void>;
  /** Load a past session (from the /chat-history page) into the live widget so
   *  the user can continue it: swaps in its messages + session id, persists it
   *  as the active session, and opens the widget. Takes the already-fetched
   *  history messages to avoid a redundant round-trip. */
  resumeSession: (sessionId: string, messages: ChatHistoryMessage[]) => void;
  submitQuery: (text: string, dashboardState?: DashboardState) => Promise<void>;
  /** Abort the in-flight SSE stream (Stop button). No-op when idle. */
  stopStreaming: () => void;
  clearGlobalFilter: () => void;
  /** Submit feedback for a specific assistant message. Optimistically
   *  updates local state, then persists to the backend. On server failure
   *  the local state is rolled back so the user can retry. */
  submitMessageFeedback: (messageId: string, feedback: ChatMessageFeedback) => Promise<boolean>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  isOpen: false,
  isWidgetEnabled: localStorage.getItem('chat_widget_disabled') !== '1',
  userId: null,
  getToken: null,
  sessionUuid: null,
  messages: [],
  isStreaming: false,
  devInspectorEnabled: localStorage.getItem('dev_inspector_disabled') !== '1',
  chatMode: (localStorage.getItem('chat_mode') === 'speed' ? 'speed' : 'quality') as 'speed' | 'quality',
  globalFilter: '',

  toggleWidget: () => set(s => ({ isOpen: !s.isOpen })),

  setWidgetEnabled: (enabled: boolean) => {
    if (enabled) localStorage.removeItem('chat_widget_disabled');
    else         localStorage.setItem('chat_widget_disabled', '1');
    set({ isWidgetEnabled: enabled, isOpen: false });
  },

  setUserId: (userId) => {
    const prev = get().userId;
    // Auth transition. A logout (→ null) or a switch to a different account
    // must not carry the previous conversation over — drop it and the stored
    // session so the user always lands on a fresh chat (restore for a returning
    // same-user refresh is handled by hydrateSession, which runs after this).
    // The first sign-in (prev === null → id) is NOT a switch: leave state alone
    // so hydrateSession can restore. Gate the caller on Clerk's isLoaded so a
    // transient null during load doesn't trip the logout branch.
    const isLogout = userId === null && prev !== null;
    const isSwitch = userId !== null && prev !== null && userId !== prev;
    if (isLogout || isSwitch) {
      activeAbort?.abort();
      activeAbort = null;
      clearPersistedSession();
      useAnchorStore.getState().clearAnchor();
      set({ userId, sessionUuid: null, messages: [], isStreaming: false, globalFilter: '' });
      return;
    }
    set({ userId });
  },
  setGetToken: (getToken) => set({ getToken }),

  setDevInspectorEnabled: (enabled: boolean) => {
    if (enabled) localStorage.removeItem('dev_inspector_disabled');
    else         localStorage.setItem('dev_inspector_disabled', '1');
    set({ devInspectorEnabled: enabled });
  },

  setChatMode: (mode) => {
    localStorage.setItem('chat_mode', mode);
    set({ chatMode: mode });
  },

  newSession: () => {
    // Abort any in-flight turn so a stale stream can't append into the
    // fresh session after the user clicked "New session".
    activeAbort?.abort();
    activeAbort = null;
    // Clear cross-page indicators set by previous queries — the
    // "Researching: …" banner at the top of the dashboard reads from
    // anchorStore, and the chat widget's yellow strip reads from
    // globalFilter. Both are user-visible context that should reset
    // alongside the conversation.
    useAnchorStore.getState().clearAnchor();
    // Drop the stored session too, so a subsequent refresh doesn't resurrect
    // the conversation the user just cleared.
    clearPersistedSession();
    set({
      sessionUuid: null,
      messages: [],
      isStreaming: false,
      globalFilter: '',
    });
  },

  hydrateSession: async () => {
    const { userId, getToken, isStreaming, messages } = get();
    // Never clobber an in-flight stream or an already-loaded conversation.
    if (isStreaming || messages.length > 0) return;
    const persisted = readPersistedSession();
    // Nothing to do when logged out / auth not settled yet (userId null), when
    // nothing is stored, or when the stored session belongs to another account.
    // Only clear on a CONFIRMED different user — never when userId is null,
    // which is also the transient "Clerk still loading" state on refresh;
    // clearing there would wipe the very session we're about to restore.
    // (A real logout clears the store + storage in setUserId.)
    if (!userId || !getToken || !persisted || persisted.userId !== userId) {
      if (userId && persisted && persisted.userId !== userId) clearPersistedSession();
      return;
    }
    try {
      const detail = await getChatSession(getToken, persisted.sessionUuid);
      // Re-check: the user may have started typing / a switch may have happened
      // while the fetch was in flight. Only apply if still safe.
      const cur = get();
      if (cur.isStreaming || cur.messages.length > 0 || cur.userId !== userId) return;
      set({ sessionUuid: persisted.sessionUuid, messages: historyToChatMessages(detail.messages) });
    } catch {
      // Expired token, session not found, or no longer owned (auth timeout /
      // logout) → forget it and let the user start a new session.
      clearPersistedSession();
    }
  },

  resumeSession: (sessionId, messages) => {
    // Abort any in-flight turn so a stale stream can't append into the session
    // we're switching to.
    activeAbort?.abort();
    activeAbort = null;
    useAnchorStore.getState().clearAnchor();
    const { userId } = get();
    // Make it the active persisted session so a subsequent refresh keeps it.
    if (userId) writePersistedSession(userId, sessionId);
    set({
      sessionUuid: sessionId,
      messages:    historyToChatMessages(messages),
      isStreaming: false,
      globalFilter: '',
      isOpen:      true,
    });
  },

  stopStreaming: () => {
    if (!get().isStreaming) return;
    activeAbort?.abort();
  },

  submitQuery: async (text, dashboardState) => {
    const { userId, getToken, sessionUuid, isStreaming, chatMode } = get();
    if (!userId || !getToken || isStreaming) return;

    useAnchorStore.getState().setAnchor(text);

    // Optimistically append the user turn and a streaming assistant placeholder.
    set(s => ({
      globalFilter: text,
      messages: [
        ...s.messages,
        { role: 'user',      text },
        { role: 'assistant', text: '', streaming: true, filterSuggestions: [], followUps: [], citations: [], linkablePeople: [], debug: [] },
      ],
      isStreaming: true,
    }));

    const ac = new AbortController();
    activeAbort = ac;

    try {
      const result = await streamChat({
        getToken,
        query: text,
        sessionId: sessionUuid,
        dashboardState: dashboardState ?? null,
        mode: chatMode,
        signal: ac.signal,

        onTextDelta: (chunk) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, text: last.text + chunk };
            }
            return { messages: msgs };
          });
        },

        // When the model calls a DATA tool mid-stream, anything it wrote
        // before the call was just "thinking out loud" (the prompt forbids
        // this but Haiku occasionally leaks). Reset the bubble so the user
        // only ever sees the FINAL iteration's text — the actual answer.
        // enrich_response is terminal and DOESN'T trigger a reset.
        onToolCall: (tool) => {
          if (tool.name === 'enrich_response') return;
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant' && last.text) {
              msgs[msgs.length - 1] = { ...last, text: '' };
            }
            return { messages: msgs };
          });
        },

        onFollowUps: (items) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, followUps: items };
            }
            return { messages: msgs };
          });
        },

        onCitations: (items) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, citations: items };
            }
            return { messages: msgs };
          });
        },

        onLinkablePeople: (items) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, linkablePeople: items };
            }
            return { messages: msgs };
          });
        },

        onDebug: (info) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, debug: [...(last.debug ?? []), info] };
            }
            return { messages: msgs };
          });
        },

        onFilterSuggestions: (items) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = { ...last, filterSuggestions: items };
            }
            return { messages: msgs };
          });
        },

        onError: (err) => {
          set(s => {
            const msgs = [...s.messages];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant') {
              msgs[msgs.length - 1] = {
                role: 'assistant',
                text: last.text || `Sorry, something went wrong (${err.code}).`,
              };
            }
            return { messages: msgs, isStreaming: false };
          });
        },
      });

      // Stream completed — finalise placeholder, capture the (possibly newly
      // created) session UUID, and attach the assistant message id so the
      // feedback UI has a target to POST to.
      // If the user aborted mid-flight, streamChat may still resolve (body
      // closed cleanly) — treat an aborted controller the same as a throw.
      if (ac.signal.aborted) {
        set(s => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant') {
            const hasText = !!last.text.trim();
            msgs[msgs.length - 1] = {
              ...last,
              streaming: false,
              // Keep any partial answer; empty bubble is flagged so the UI
              // can show a localised “Stopped.” placeholder.
              stopped: !hasText,
            };
          }
          return { messages: msgs, isStreaming: false };
        });
        return;
      }

      set(s => {
        const msgs = [...s.messages];
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant') {
          msgs[msgs.length - 1] = {
            ...last,
            streaming: false,
            messageId: result.messageId ?? last.messageId,
          };
        }
        const sessionUuid = result.sessionId || s.sessionUuid;
        // Persist so a refresh can restore this conversation. Scoped to the
        // user; only when signed in (a null userId means we can't safely
        // re-own it on restore).
        if (sessionUuid && s.userId) writePersistedSession(s.userId, sessionUuid);
        return {
          messages:    msgs,
          isStreaming: false,
          sessionUuid,
        };
      });
    } catch (err) {
      if (isAbortError(err) || ac.signal.aborted) {
        set(s => {
          const msgs = [...s.messages];
          const last = msgs[msgs.length - 1];
          if (last?.role === 'assistant') {
            const hasText = !!last.text.trim();
            msgs[msgs.length - 1] = {
              ...last,
              streaming: false,
              stopped: !hasText,
            };
          }
          return { messages: msgs, isStreaming: false };
        });
        return;
      }
      set(s => {
        const msgs = [...s.messages];
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant') {
          msgs[msgs.length - 1] = {
            role: 'assistant',
            text: 'Sorry, something went wrong. Please try again.',
          };
        }
        return { messages: msgs, isStreaming: false };
      });
    } finally {
      if (activeAbort === ac) activeAbort = null;
    }
  },

  clearGlobalFilter: () => set({ globalFilter: '' }),

  submitMessageFeedback: async (messageId, feedback) => {
    const { getToken } = get();
    if (!getToken) return false;

    // Optimistic local update — flip the affordance immediately so the
    // user gets feedback (no pun) even on a slow network.
    const previous: ChatMessageFeedback | undefined = (() => {
      const m = get().messages.find(msg => msg.messageId === messageId);
      return m?.feedback;
    })();

    set(s => ({
      messages: s.messages.map(msg =>
        msg.messageId === messageId ? { ...msg, feedback } : msg
      ),
    }));

    const ok = await submitFeedback(messageId, getToken, {
      rating:   feedback.rating,
      category: feedback.category ?? undefined,
      comment:  feedback.comment ?? undefined,
    }).catch(() => false);

    if (!ok) {
      // Roll back so the user can retry — visible failure is better than
      // silent loss of a thumbs-down they expected to record.
      set(s => ({
        messages: s.messages.map(msg =>
          msg.messageId === messageId ? { ...msg, feedback: previous } : msg
        ),
      }));
    }
    return ok;
  },
}));
